import { State } from "@features/base/types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminUsersResponse,
  AdminUsersRequest,
  getAdminUsersApi,
  AdminUser,
  UserInfoBalance,
  getAdminUserInfoApi,
  UserInfoResponse,
  UserInfoUpdateRequest,
  updateUserInfoApi,
  changeUserBalanceApi,
  UserChangeBalanceRequest,
  ChangePasswordRequest,
  changeUserPasswordApi,
  resetUserTOTPRequest,
  resetUserTOTPApi,
  AdminUserRolesResponse,
  getAdminUserRolesApi,
  AdminUserRole,
  AddMerchantWalletsRequest,
  addMerchantWalletsApi,
  ActivateUserRequest,
  activateUserApi,
  UpdateUserNotificationSettingsRequest,
  updateUserNotificationSettingsApi,
  SetUserRoleRequest,
  setUserRoleApi,
  AdminUserWallet,
  getApiKeyApi,
  CommissionConfig,
  UserInfoEarnBalance,
  ReferralDetails,
  PlatformMerchantInfo,
  AdminUserTypes,
  PlatformTransactionInfo,
  PlatformTransactionsResponse,
  getPlatformTransactionsApi,
  addMerchantToPlatformApi,
  AddMerchantToPlatformRequest,
  EditMerchantToPlatformRequest,
  editMerchantToPlatformApi,
  AddTransactionToPlatformRequest,
  addTransactionToPlatformApi,
  setPlatformTeamleadComissionApi,
  SetPlatformTeamleadComissionRequest,
} from "./api";

export const getAdminUsers = createAsyncThunk<
  AdminUsersResponse,
  { data?: AdminUsersRequest }
>("adminUsers/getAdminUsers", async ({ data }, { rejectWithValue }) => {
  try {
    return await getAdminUsersApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getAdminUserInfo = createAsyncThunk<
  UserInfoResponse,
  { userId: string }
>("adminUsers/getAdminUserInfo", async ({ userId }, { rejectWithValue }) => {
  try {
    const response = await getAdminUserInfoApi(userId);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateAdminUserInfo = createAsyncThunk<
  string,
  { userId: string; data: UserInfoUpdateRequest }
>(
  "adminUsers/updateUserInfo",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await updateUserInfoApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const changeUserBalance = createAsyncThunk<
  string,
  { balanceId: string; data: UserChangeBalanceRequest }
>(
  "adminUsers/changeUserBalance",
  async ({ balanceId, data }, { rejectWithValue }) => {
    try {
      return await changeUserBalanceApi(balanceId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const changeUserPassword = createAsyncThunk<
  string,
  { data: ChangePasswordRequest }
>("adminUsers/changeUserPassword", async ({ data }, { rejectWithValue }) => {
  try {
    return await changeUserPasswordApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const resetUserTOTP = createAsyncThunk<
  string,
  { data: resetUserTOTPRequest }
>("adminUsers/resetUserTOTP", async ({ data }, { rejectWithValue }) => {
  try {
    return await resetUserTOTPApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getAdminUserRoles = createAsyncThunk<
  AdminUserRolesResponse,
  undefined
>("adminUsers/getAdminUserRoles", async (_, { rejectWithValue }) => {
  try {
    return await getAdminUserRolesApi();
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const addMerchantWallets = createAsyncThunk<
  string,
  { data: AddMerchantWalletsRequest }
>("adminUsers/addMerchantWallets", async ({ data }, { rejectWithValue }) => {
  try {
    return await addMerchantWalletsApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const activateUser = createAsyncThunk<
  string,
  { data: ActivateUserRequest }
>("adminUsers/activateUser", async ({ data }, { rejectWithValue }) => {
  try {
    return await activateUserApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateUserNotificationSettings = createAsyncThunk<
  string,
  { userId: string; data: UpdateUserNotificationSettingsRequest }
>(
  "adminUsers/updateUserNotificationSettings",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await updateUserNotificationSettingsApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const setUserRole = createAsyncThunk<
  string,
  { userId: string; data: SetUserRoleRequest }
>(
  "adminUsers/SetUserRoleRequest",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await setUserRoleApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getApiKey = createAsyncThunk<string, { userId: string }>(
  "adminUsers/getApiKey",
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await getApiKeyApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPlatformTransactions = createAsyncThunk<
  PlatformTransactionsResponse,
  { userId: string }
>(
  "adminUsers/getPlatformTransactions",
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await getPlatformTransactionsApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addMerchantToPlatform = createAsyncThunk<
  string,
  { userId: string; data: AddMerchantToPlatformRequest }
>(
  "adminUsers/addMerchantToPlatform",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await addMerchantToPlatformApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editMerchantToPlatform = createAsyncThunk<
  string,
  { userId: string; data: EditMerchantToPlatformRequest }
>(
  "adminUsers/editMerchantToPlatformApi",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await editMerchantToPlatformApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addTransactionToPlatform = createAsyncThunk<
  string,
  { userId: string; data: AddTransactionToPlatformRequest }
>(
  "adminUsers/addTransactionToPlatform",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await addTransactionToPlatformApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const setPlatformTeamleadComission = createAsyncThunk<
  string,
  { userId: string; data: SetPlatformTeamleadComissionRequest }
>(
  "adminUsers/setPlatformTeamleadComission",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await setPlatformTeamleadComissionApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

type BaseAdminUserInfo = {
  role?: string;
  email?: string;
  status: string | null;
  tg_group_id: number | null;
};

type TraderAdminUserInfo = BaseAdminUserInfo & {
  kind: AdminUserTypes.Trader;
  allow_farm_creation: boolean;
  allow_requisites_without_device: boolean;
  boosted: boolean;
  commission_config: CommissionConfig;
  ern_balance: UserInfoEarnBalance;
  frozen_balance: UserInfoBalance;
  save_balance: UserInfoBalance;
  trust_balance: UserInfoBalance;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  referrer_email: string | null;
  referrer_id: string | null;
  team_lead_commission_pay_in: number;
};

type MerchantAdminUserInfo = BaseAdminUserInfo & {
  kind: AdminUserTypes.Merchant;
  balance: UserInfoBalance;
  commission_config: CommissionConfig;
  commission_for_trader_pay_out: number | null;
  commission_pay_out: number;
  commission_withdrawal: number;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  referrer_email: string | null;
  referrer_id: string | null;
  team_lead_commission_pay_in: number | null;
  wallets: AdminUserWallet[] | null;
};

type LeadAdminUserInfo = BaseAdminUserInfo & {
  kind: AdminUserTypes.Lead;
  commission_withdrawal: number | null;
  referral_details: ReferralDetails[];
  total_pay_in_usdt: number;
  total_pay_out_usdt: number;
  total_usdt: number;
  wallets: AdminUserWallet[] | null;
};

type PlatformAdminUserInfo = BaseAdminUserInfo & {
  kind: AdminUserTypes.Platform;
  disputes_approved: number;
  disputes_canceled: number;
  disputes_opened: number;
  frozen_balance: number;
  id: string;
  merchants: PlatformMerchantInfo[];
  orders_completed: number;
  orders_canceled?: number;
  orders_with_out_requisite_1000: number;
  orders_with_out_requisite_3000: number;
  orders_with_out_requisite_5000: number;
  orders_with_out_requisite_10000: number;
  requests_with_requisites: number;
  requests_without_requisites: number;
  send_orders_1000: number;
  send_orders_3000: number;
  send_orders_5000: number;
  send_orders_10000: number;
  token_for_callback: string;
  total_requests: number;
  trust_balance: number;
  transactions?: PlatformTransactionInfo[];
};

type AdminUserInfo =
  | TraderAdminUserInfo
  | MerchantAdminUserInfo
  | LeadAdminUserInfo
  | PlatformAdminUserInfo
  | null;

type AdminUsersState = State<{
  apiKey: string | null;
  roles: AdminUserRole[];
  users: AdminUser[] | null;
  // selectedUser: AdminUser | null;
  userInfo: AdminUserInfo | null;
  page: number;
  count: number;
  selectedBalanceType: string;
  balanceFinalAmount: number;
  isModalLoading: boolean;
  isPlatformTransactionsLoading: boolean;
  platformTransactions: PlatformTransactionInfo[] | null;
  platformTransactionsPage: number;
  platformTransactionsCount: number;
  platformModalLoading: boolean;
}>;

const initialState: AdminUsersState = {
  roles: [],
  apiKey: null,
  users: null,
  // selectedUser: null,
  loading: false,
  error: null,
  selectedBalanceType: "",
  balanceFinalAmount: 0,
  isModalLoading: false,
  userInfo: null,
  page: 1,
  count: 0,
  isPlatformTransactionsLoading: false,
  platformTransactions: null,
  platformTransactionsPage: 1,
  platformTransactionsCount: 0,
  platformModalLoading: false,
};

const adminUsersSlice = createSlice({
  name: "adminUsers",
  initialState,
  reducers: {
    updateData: (state, action: PayloadAction<UserInfoUpdateRequest>) => {
      if (state.userInfo) {
        const payload = action.payload;
        state.userInfo.status = payload.status ?? "";

        if (
          (state.userInfo.kind === AdminUserTypes.Trader ||
            state.userInfo.kind === AdminUserTypes.Merchant) &&
          (payload.kind == AdminUserTypes.Merchant ||
            payload.kind == AdminUserTypes.Trader)
        ) {
          state.userInfo.referrer_email = payload.referrer_email ?? null;
          state.userInfo.commission_config = {
            ...(state.userInfo.commission_config ?? {
              commission_sbp: [],
              commission_card: [],
              commission_account_number: [],
            }),
            ...payload.commission_config,
          };
          state.userInfo.pay_in = payload.pay_in;
          state.userInfo.pay_out = payload.pay_out;
          state.userInfo.min_sum_order = payload.min_sum_order;
          state.userInfo.max_sum_order = payload.max_sum_order;
        }

        if (
          (state.userInfo.kind == AdminUserTypes.Lead ||
            state.userInfo.kind == AdminUserTypes.Merchant) &&
          (payload.kind == AdminUserTypes.Lead ||
            payload.kind == AdminUserTypes.Merchant)
        ) {
          state.userInfo.commission_withdrawal =
            payload.commission_withdrawal ?? null;
        }
        if (
          state.userInfo.kind == AdminUserTypes.Merchant &&
          payload.kind == AdminUserTypes.Merchant
        ) {
          state.userInfo.commission_pay_out = payload.commission_pay_out;
        }

        if (
          state.userInfo.kind == AdminUserTypes.Trader &&
          payload.kind == AdminUserTypes.Trader
        ) {
          state.userInfo.allow_farm_creation =
            payload.allow_farm_creation ?? null;
          state.userInfo.boosted = payload.boosted ?? null;
          state.userInfo.allow_requisites_without_device =
            payload.allow_requisites_without_device ?? null;
        }
      }
    },
    resetData: (state) => {
      state.userInfo = null;
    },
    setSelectedBalanceType: (
      state,
      action: PayloadAction<{ type: string; amount: number }>
    ) => {
      state.selectedBalanceType = action.payload.type;
      state.balanceFinalAmount = action.payload.amount;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.page = action.payload.page;
        state.count = action.payload.count;
      })
      .addCase(getAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAdminUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload == null) {
          state.userInfo = null;
          return;
        }

        switch (payload.kind) {
          case "trader":
            state.userInfo = { ...payload } as TraderAdminUserInfo;
            break;
          case "merchant":
            state.userInfo = { ...payload } as MerchantAdminUserInfo;
            break;
          case "lead":
            state.userInfo = { ...payload } as LeadAdminUserInfo;
            break;
          case "platform":
            state.userInfo = { ...payload } as PlatformAdminUserInfo;
            break;
          default:
            state.userInfo = payload as AdminUserInfo;
            break;
        }
      })
      .addCase(getAdminUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateAdminUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
      })
      .addCase(updateAdminUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(changeUserBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserBalance.fulfilled, (state) => {
        state.loading = false;
        // console.log(action.payload);
        if (state.userInfo?.kind == AdminUserTypes.Trader) {
          switch (state.selectedBalanceType) {
            case "trust":
              if (state.userInfo.trust_balance)
                state.userInfo.trust_balance.amount = state.balanceFinalAmount;
              break;
            case "frozen":
              if (state.userInfo.frozen_balance)
                state.userInfo.frozen_balance.amount = state.balanceFinalAmount;
              break;
            case "Ern_Pay_IN":
              if (state.userInfo.ern_balance)
                state.userInfo.ern_balance.pay_in = state.balanceFinalAmount;
              break;
            case "Ern_Pay_OUT":
              if (state.userInfo.ern_balance)
                state.userInfo.ern_balance.pay_out = state.balanceFinalAmount;
              break;
            case "save":
              if (state.userInfo.save_balance)
                state.userInfo.save_balance.amount = state.balanceFinalAmount;
              break;
          }
        }
      })
      .addCase(changeUserBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resetUserTOTP.pending, (state) => {
        state.isModalLoading = true;
        state.error = null;
      })
      .addCase(resetUserTOTP.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(resetUserTOTP.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAdminUserRoles.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(getAdminUserRoles.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(addMerchantWallets.pending, (state) => {
        state.isModalLoading = true;
      })
      .addCase(addMerchantWallets.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(addMerchantWallets.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
        console.log(action.payload);
      })
      .addCase(activateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateUser.fulfilled, (_, action) => {
        console.log(action.payload);
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.error = action.payload as string;
        console.log(action.payload);
      })
      .addCase(updateUserNotificationSettings.fulfilled, (_, action) => {
        console.log(action.payload);
      })
      .addCase(updateUserNotificationSettings.rejected, (state, action) => {
        state.error = action.payload as string;
        console.log(action.payload);
      })
      .addCase(setUserRole.pending, (state) => {
        state.isModalLoading = true;
      })
      .addCase(setUserRole.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(setUserRole.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
        console.log(action.payload);
      })
      .addCase(getApiKey.pending, (state) => {
        state.apiKey = null;
      })
      .addCase(getApiKey.fulfilled, (state, action) => {
        state.apiKey = action.payload;
      })
      .addCase(getApiKey.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getPlatformTransactions.pending, (state) => {
        state.platformTransactions = null;
        state.isPlatformTransactionsLoading = true;
      })
      .addCase(getPlatformTransactions.fulfilled, (state, action) => {
        state.platformTransactions = action.payload.transactions;
        state.platformTransactionsPage = action.payload.page;
        state.platformTransactionsCount = action.payload.count;
        state.isPlatformTransactionsLoading = false;
      })
      .addCase(getPlatformTransactions.rejected, (state, action) => {
        // state.error = action.payload as string;
        state.isPlatformTransactionsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addMerchantToPlatform.pending, (state) => {
        state.platformModalLoading = true;
      })
      .addCase(addMerchantToPlatform.fulfilled, (state, action) => {
        state.platformModalLoading = false;
        console.log(action.payload);
      })
      .addCase(addMerchantToPlatform.rejected, (state, action) => {
        // state.error = action.payload as string;
        state.platformModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(editMerchantToPlatform.pending, (state) => {
        state.platformModalLoading = true;
      })
      .addCase(editMerchantToPlatform.fulfilled, (state, action) => {
        state.platformModalLoading = false;
        console.log(action.payload);
      })
      .addCase(editMerchantToPlatform.rejected, (state, action) => {
        // state.error = action.payload as string;
        state.platformModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addTransactionToPlatform.pending, (state) => {
        state.platformModalLoading = true;
      })
      .addCase(addTransactionToPlatform.fulfilled, (state, action) => {
        state.platformModalLoading = false;
        console.log(action.payload);
      })
      .addCase(addTransactionToPlatform.rejected, (state, action) => {
        // state.error = action.payload as string;
        state.platformModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(setPlatformTeamleadComission.pending, (state) => {
        state.platformModalLoading = true;
      })
      .addCase(setPlatformTeamleadComission.fulfilled, (state, action) => {
        state.platformModalLoading = false;
        console.log(action.payload);
      })
      .addCase(setPlatformTeamleadComission.rejected, (state, action) => {
        // state.error = action.payload as string;
        state.platformModalLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { updateData, resetData, setSelectedBalanceType } =
  adminUsersSlice.actions;
export default adminUsersSlice.reducer;
